/**
 * SalesBrochureDialog — Modal de configuration paramétrable pour le Plan de Vente
 *   • Upload logo client (project-level, fallback org)
 *   • Couleur d'accent (color picker)
 *   • Format papier (A4P/L, A3P/L)
 *   • Zone widths (sliders left/center/right)
 *   • Blocs contenu (checkboxes)
 *   • Filtres lots (buildings, typologies, statuts)
 *   • Tri
 *   • Page de garde optionnelle
 *   • Boutons : Sauvegarder config · Générer PDF
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Settings, Upload, FileDown, Loader2, Save, Image as ImageIcon, X, Palette, Eye,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAPER_FORMATS = [
  { id: 'A4_landscape', label: 'A4 Paysage' },
  { id: 'A4_portrait',  label: 'A4 Portrait' },
  { id: 'A3_landscape', label: 'A3 Paysage' },
  { id: 'A3_portrait',  label: 'A3 Portrait' },
];

const SORT_OPTIONS = [
  { id: 'lot_number', label: 'Numéro de lot' },
  { id: 'typology',   label: 'Typologie' },
  { id: 'surface',    label: 'Surface' },
];

// Presets de marges (mm) — appliquent les 4 marges + gouttière en un clic.
const MARGIN_PRESETS = {
  compact:  { top: 6,  right: 6,  bottom: 6,  left: 6,  gutter: 6 },
  standard: { top: 8,  right: 8,  bottom: 8,  left: 8,  gutter: 8 },
  aere:     { top: 14, right: 14, bottom: 14, left: 14, gutter: 12 },
  magazine: { top: 20, right: 20, bottom: 20, left: 20, gutter: 16 },
};
const MARGIN_PRESET_LABELS = {
  compact:  'Compact',
  standard: 'Standard',
  aere:     'Aéré',
  magazine: 'Magazine',
  custom:   'Personnalisé',
};
const DEFAULT_MARGINS = MARGIN_PRESETS.standard;

const DEFAULT_BLOCKS = {
  lot_plan_2d: true,
  shoebox: true,
  fps: false,
  building_facade: false,
  surface_table: true,
  description: true,
  amenities: false,
  extra_thumbnails: false,
  info_surface_total: true,
  info_surface_terrasse: true,
  info_surface_annexes: false,
  info_orientation: false,
  info_floor: true,
  info_style: false,
  info_materials: false,
};

const BLOCK_LABELS = {
  lot_plan_2d: 'Plan 2D du lot',
  shoebox: 'Rendu Shoebox 3D',
  fps: 'Rendu FPS (perspectives)',
  building_facade: 'Façade du bâtiment',
  surface_table: 'Tableau des surfaces',
  description: 'Description du lot',
  amenities: 'Équipements / amenities',
  extra_thumbnails: 'Strip mini-thumbnails bas',
  info_surface_total: 'Surface totale',
  info_surface_terrasse: 'Surface terrasse / balcon',
  info_surface_annexes: 'Surface annexes (parking, cave)',
  info_orientation: 'Orientation',
  info_floor: 'Étage',
  info_style: 'Style de décoration',
  info_materials: 'Matériaux retenus',
};

export default function SalesBrochureDialog({ projectId, projectName, onClose }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Config state
  const [paperFormat, setPaperFormat] = useState('A4_landscape');
  const [accentColor, setAccentColor] = useState('#D9A05B');
  const [logoUrl, setLogoUrl] = useState(null);     // project-level
  const [orgLogo, setOrgLogo] = useState(null);     // org-level fallback
  const [includeCover, setIncludeCover] = useState(false);
  const [zoneWidths, setZoneWidths] = useState({ left: 0.38, center: 0.34, right: 0.28 });
  const [blocks, setBlocks] = useState(DEFAULT_BLOCKS);
  const [selectedBuildings, setSelectedBuildings] = useState([]);
  const [selectedTypologies, setSelectedTypologies] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [sortBy, setSortBy] = useState('lot_number');
  // Data from server
  const [availableBuildings, setAvailableBuildings] = useState([]);
  const [availableTypologies, setAvailableTypologies] = useState([]);
  const [availableStatuses, setAvailableStatuses] = useState([]);
  // Cover assets + custom images
  const [coverPerspectivePath, setCoverPerspectivePath] = useState(null);
  const [coverPlanMassePath, setCoverPlanMassePath] = useState(null);
  const [customImages, setCustomImages] = useState([]);
  const [uploadingAsset, setUploadingAsset] = useState(null); // 'perspective' | 'plan_masse' | 'custom' | null
  // Legend
  const [includeLegend, setIncludeLegend] = useState(false);
  const [includeLotLocation, setIncludeLotLocation] = useState(false);
  const [legendItems, setLegendItems] = useState([
    { code: 'VR', label: 'Volet roulant' },
    { code: 'TE', label: 'Terrasse' },
    { code: 'R', label: 'Rangement' },
    { code: 'SS', label: 'Sèche-serviettes' },
  ]);

  // Marges configurables (mm) + preset actif
  const [margins, setMargins] = useState(DEFAULT_MARGINS);
  const [marginPreset, setMarginPreset] = useState('standard');

  // Live preview
  const [previewEnabled, setPreviewEnabled] = useState(true);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const previewTimerRef = useRef(null);
  const previewCancelRef = useRef(null);

  const loadConfig = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/projects/${projectId}/sales-brochure-config`, {
        withCredentials: true,
      });
      const cfg = r.data?.config || {};
      if (cfg.paper_format) setPaperFormat(cfg.paper_format);
      if (cfg.accent_color) setAccentColor(cfg.accent_color);
      if (cfg.logo_url) setLogoUrl(cfg.logo_url);
      else if (r.data?.project_logo_url) setLogoUrl(r.data.project_logo_url);
      if (cfg.zone_widths) setZoneWidths({ ...zoneWidths, ...cfg.zone_widths });
      if (cfg.blocks) setBlocks({ ...DEFAULT_BLOCKS, ...cfg.blocks });
      if (cfg.filters) {
        setSelectedBuildings(cfg.filters.building_ids || []);
        setSelectedTypologies(cfg.filters.typologies || []);
        setSelectedStatuses(cfg.filters.workflow_statuses || []);
      }
      if (cfg.include_cover) setIncludeCover(true);
      if (cfg.include_legend) setIncludeLegend(true);
      if (cfg.include_lot_location) setIncludeLotLocation(true);
      if (Array.isArray(cfg.legend_items) && cfg.legend_items.length) setLegendItems(cfg.legend_items);
      if (cfg.sort_by) setSortBy(cfg.sort_by);
      if (cfg.margins && typeof cfg.margins === 'object') {
        const m = { ...DEFAULT_MARGINS, ...cfg.margins };
        setMargins(m);
        // Détecter automatiquement un preset connu
        const matched = Object.entries(MARGIN_PRESETS).find(([, p]) =>
          ['top','right','bottom','left','gutter'].every((k) => Number(p[k]) === Number(m[k]))
        );
        setMarginPreset(matched ? matched[0] : 'custom');
      }
      setOrgLogo(r.data?.org_logo_url || null);
      setAvailableBuildings(r.data?.available_buildings || []);
      setAvailableTypologies(r.data?.available_typologies || []);
      setAvailableStatuses(r.data?.available_workflow_statuses || []);
      setCoverPerspectivePath(r.data?.cover_perspective_path || null);
      setCoverPlanMassePath(r.data?.cover_plan_masse_path || null);
      setCustomImages(r.data?.brochure_custom_images || []);
    } catch (e) {
      toast.error('Erreur chargement config');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const collectConfig = () => ({
    paper_format: paperFormat,
    accent_color: accentColor,
    logo_url: logoUrl,
    include_cover: includeCover,
    include_legend: includeLegend,
    include_lot_location: includeLotLocation,
    legend_items: legendItems,
    zone_widths: zoneWidths,
    blocks,
    filters: {
      building_ids: selectedBuildings,
      typologies: selectedTypologies,
      workflow_statuses: selectedStatuses,
    },
    sort_by: sortBy,
    margins,
  });

  // Helpers marges
  const applyMarginPreset = (preset) => {
    setMarginPreset(preset);
    if (MARGIN_PRESETS[preset]) setMargins(MARGIN_PRESETS[preset]);
  };
  const updateMargin = (key, value) => {
    setMargins((p) => {
      const next = { ...p, [key]: value };
      // Si l'utilisateur dévie d'un preset → bascule sur "Personnalisé"
      const matched = Object.entries(MARGIN_PRESETS).find(([, pp]) =>
        ['top','right','bottom','left','gutter'].every((k) => Number(pp[k]) === Number(next[k]))
      );
      setMarginPreset(matched ? matched[0] : 'custom');
      return next;
    });
  };

  // ---------- Live preview (1 lot, debounce 800ms) ----------
  // Build a stable signature of all config values that affect rendering
  const previewSignature = JSON.stringify({
    paperFormat, accentColor, logoUrl, includeLegend, includeLotLocation,
    legendItems, zoneWidths, blocks, margins,
  });

  useEffect(() => {
    if (loading || !previewEnabled) return;
    // Debounce 800ms — cancel previous timer + previous in-flight request
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    if (previewCancelRef.current) previewCancelRef.current.abort();

    previewTimerRef.current = setTimeout(async () => {
      const controller = new AbortController();
      previewCancelRef.current = controller;
      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const r = await axios.post(
          `${API}/projects/${projectId}/export-sales-pdf`,
          { config: collectConfig(), preview: true },
          {
            withCredentials: true,
            responseType: 'blob',
            timeout: 45000,
            signal: controller.signal,
          },
        );
        const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
        // Revoke previous blob URL to avoid memory leaks
        setPreviewUrl((prev) => {
          if (prev) window.URL.revokeObjectURL(prev);
          return url;
        });
      } catch (e) {
        if (e?.code === 'ERR_CANCELED' || e?.name === 'CanceledError') return;
        // Quand on demande responseType:'blob', les erreurs reviennent aussi en blob → décoder pour récupérer le message
        let detail = 'Erreur prévisualisation';
        const errBlob = e?.response?.data;
        if (errBlob && typeof errBlob.text === 'function') {
          try {
            const txt = await errBlob.text();
            try {
              const parsed = JSON.parse(txt);
              if (parsed?.detail) detail = parsed.detail;
            } catch {
              if (txt) detail = txt.slice(0, 180);
            }
          } catch {
            /* ignore */
          }
        } else if (typeof errBlob?.detail === 'string') {
          detail = errBlob.detail;
        }
        setPreviewError(detail);
      } finally {
        setPreviewLoading(false);
      }
    }, 800);

    return () => {
      if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewSignature, loading, previewEnabled]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrl) window.URL.revokeObjectURL(previewUrl);
      if (previewCancelRef.current) previewCancelRef.current.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cover assets uploaders
  const uploadCoverAsset = async (type, file) => {
    if (!file) return;
    setUploadingAsset(type);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/projects/${projectId}/brochure-cover-asset?asset_type=${type}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      if (type === 'perspective') setCoverPerspectivePath(r.data.path);
      else setCoverPlanMassePath(r.data.path);
      toast.success(type === 'perspective' ? 'Perspective uploadée' : 'Plan masse uploadé');
    } catch {
      toast.error('Erreur upload');
    } finally {
      setUploadingAsset(null);
    }
  };
  const deleteCoverAsset = async (type) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/brochure-cover-asset/${type}`, { withCredentials: true });
      if (type === 'perspective') setCoverPerspectivePath(null);
      else setCoverPlanMassePath(null);
      toast.success('Supprimé');
    } catch {
      toast.error('Erreur suppression');
    }
  };
  const uploadCustomImage = async (file, label) => {
    if (!file) return;
    setUploadingAsset('custom');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(
        `${API}/projects/${projectId}/brochure-custom-image${label ? `?label=${encodeURIComponent(label)}` : ''}`,
        fd,
        { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setCustomImages((p) => [...p, r.data]);
      toast.success('Image ajoutée à la galerie');
    } catch {
      toast.error('Erreur upload');
    } finally {
      setUploadingAsset(null);
    }
  };
  const deleteCustomImage = async (image_id) => {
    try {
      await axios.delete(`${API}/projects/${projectId}/brochure-custom-image/${image_id}`, { withCredentials: true });
      setCustomImages((p) => p.filter((img) => img.image_id !== image_id));
    } catch {
      toast.error('Erreur suppression');
    }
  };
  const storageImg = (path) =>
    path ? `${process.env.REACT_APP_BACKEND_URL}/api/file-storage/${encodeURIComponent(path)}` : null;

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Logo trop volumineux (max 5 Mo)');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/projects/${projectId}/sales-brochure-logo`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoUrl(r.data?.logo_url);
      toast.success('Logo uploadé');
    } catch {
      toast.error('Erreur upload logo');
    } finally {
      setUploading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${projectId}/sales-brochure-config`,
        { config: collectConfig() },
        { withCredentials: true });
      toast.success('Configuration sauvegardée');
    } catch {
      toast.error('Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post(
        `${API}/projects/${projectId}/export-sales-pdf`,
        { config: collectConfig(), save_config: true },
        { withCredentials: true, responseType: 'blob', timeout: 90000 },
      );
      const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `PlanDeVente_${(projectName || 'projet').replace(/\s/g, '_')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF généré !');
    } catch {
      toast.error('Erreur génération PDF');
    } finally {
      setGenerating(false);
    }
  };

  const toggleArray = (arr, setter, id) => {
    setter(arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]);
  };

  const toggleBlock = (k) => setBlocks((p) => ({ ...p, [k]: !p[k] }));

  // Effective logo to display (project > org)
  const effectiveLogo = logoUrl || orgLogo;
  const logoSrc = effectiveLogo
    ? (effectiveLogo.startsWith('http') ? effectiveLogo : `${process.env.REACT_APP_BACKEND_URL}/api/file-storage/${encodeURIComponent(effectiveLogo)}`)
    : null;

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-[1280px] w-[95vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="sales-brochure-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Settings className="w-5 h-5 text-amber-600" />
            Configurer le Plan de Vente
          </DialogTitle>
          <DialogDescription>
            Personnalise ton plan de vente : logo, couleurs, contenus, format papier et filtres de lots.
            <span className="ml-2 inline-flex items-center gap-1 text-amber-700">
              <Eye className="w-3 h-3" />
              <span className="text-[11px] font-medium">Aperçu en direct (1 lot exemple)</span>
            </span>
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-6 h-6 animate-spin text-amber-600" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,440px)] gap-6 flex-1 min-h-0">
            <div className="space-y-6 overflow-y-auto pr-2" data-testid="sales-config-panel">
            {/* Identity */}
            <section>
              <h3 className="text-sm font-semibold mb-3 text-gray-800">🎨 Identité visuelle</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Logo */}
                <div>
                  <Label className="text-xs">Logo client</Label>
                  <div className="mt-1 flex items-center gap-3">
                    {logoSrc ? (
                      <img src={logoSrc} alt="logo" className="w-16 h-16 rounded object-contain bg-white border" />
                    ) : (
                      <div className="w-16 h-16 rounded border bg-gray-50 flex items-center justify-center">
                        <ImageIcon className="w-6 h-6 text-gray-300" />
                      </div>
                    )}
                    <div className="flex-1">
                      <label className="cursor-pointer">
                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-testid="sales-logo-input" />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={uploading}
                          data-testid="sales-logo-upload-btn"
                          asChild
                        >
                          <span>
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                            {logoUrl ? 'Remplacer' : 'Uploader un logo'}
                          </span>
                        </Button>
                      </label>
                      {logoUrl && (
                        <button
                          type="button"
                          onClick={() => setLogoUrl(null)}
                          className="text-xs text-red-500 ml-2"
                          data-testid="sales-logo-remove"
                        >
                          retirer (utiliser celui de l'organisation)
                        </button>
                      )}
                      <p className="text-[10px] text-gray-400 mt-1">PNG/JPG max 5 Mo · fallback : logo organisation</p>
                    </div>
                  </div>
                </div>

                {/* Accent color */}
                <div>
                  <Label className="text-xs flex items-center gap-1">
                    <Palette className="w-3 h-3" /> Couleur d'accent
                  </Label>
                  <div className="mt-1 flex items-center gap-2">
                    <input
                      type="color"
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="w-12 h-10 rounded border cursor-pointer"
                      data-testid="sales-accent-color"
                    />
                    <Input
                      value={accentColor}
                      onChange={(e) => setAccentColor(e.target.value)}
                      className="flex-1 font-mono text-xs"
                      data-testid="sales-accent-hex"
                    />
                  </div>
                </div>
              </div>
            </section>

            {/* Format & layout */}
            <section>
              <h3 className="text-sm font-semibold mb-3 text-gray-800">📐 Format & layout</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs">Format papier</Label>
                  <Select value={paperFormat} onValueChange={setPaperFormat}>
                    <SelectTrigger data-testid="sales-paper-format">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PAPER_FORMATS.map((f) => (
                        <SelectItem key={f.id} value={f.id}>{f.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center justify-between rounded-lg border p-3 bg-gray-50">
                  <div>
                    <Label className="text-xs">Page de garde projet</Label>
                    <p className="text-[10px] text-gray-500">Avec nom résidence, adresse, récap lots</p>
                  </div>
                  <Switch checked={includeCover} onCheckedChange={setIncludeCover} data-testid="sales-include-cover" />
                </div>
              </div>

              {/* Cover assets uploaders (visible if includeCover ON) */}
              {includeCover && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                  <AssetSlot
                    label="📷 Photo perspective extérieure"
                    src={storageImg(coverPerspectivePath)}
                    uploading={uploadingAsset === 'perspective'}
                    onUpload={(f) => uploadCoverAsset('perspective', f)}
                    onDelete={() => deleteCoverAsset('perspective')}
                    testid="cover-perspective"
                  />
                  <AssetSlot
                    label="🗺️ Plan masse / situation"
                    src={storageImg(coverPlanMassePath)}
                    uploading={uploadingAsset === 'plan_masse'}
                    onUpload={(f) => uploadCoverAsset('plan_masse', f)}
                    onDelete={() => deleteCoverAsset('plan_masse')}
                    testid="cover-plan-masse"
                  />
                </div>
              )}

              <div className="mt-4">
                <Label className="text-xs">Répartition des zones (page lot)</Label>
                <div className="grid grid-cols-3 gap-3 mt-2">
                  {[
                    { key: 'left', label: 'Plan 2D (gauche)' },
                    { key: 'center', label: 'Rendu 3D (centre)' },
                    { key: 'right', label: 'Infos (droite)' },
                  ].map((z) => (
                    <div key={z.key} className="rounded border p-2">
                      <p className="text-[10px] text-gray-500">{z.label}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Slider
                          value={[Math.round(zoneWidths[z.key] * 100)]}
                          onValueChange={(v) => setZoneWidths((p) => ({ ...p, [z.key]: v[0] / 100 }))}
                          min={15}
                          max={60}
                          step={2}
                          data-testid={`sales-zone-${z.key}`}
                        />
                        <span className="text-xs tabular-nums w-10 text-right">{Math.round(zoneWidths[z.key] * 100)}%</span>
                      </div>
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-1">
                  Total: {Math.round((zoneWidths.left + zoneWidths.center + zoneWidths.right) * 100)}% (idéal 100%)
                </p>
              </div>

              {/* Marges configurables (mm) */}
              <div className="mt-4 rounded-lg border p-3 bg-amber-50/40">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-xs font-semibold text-gray-800">📏 Marges & gouttière (mm)</Label>
                  <div className="flex items-center gap-1.5" data-testid="sales-margin-presets">
                    {['compact', 'standard', 'aere', 'magazine'].map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => applyMarginPreset(p)}
                        data-testid={`sales-margin-preset-${p}`}
                        className="text-[11px] px-2 py-0.5 rounded-full border transition-colors"
                        style={
                          marginPreset === p
                            ? { background: accentColor, color: '#fff', border: `1px solid ${accentColor}` }
                            : { background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb' }
                        }
                      >
                        {MARGIN_PRESET_LABELS[p]}
                      </button>
                    ))}
                    {marginPreset === 'custom' && (
                      <span
                        className="text-[10px] px-2 py-0.5 rounded-full bg-gray-200 text-gray-600"
                        data-testid="sales-margin-preset-custom-badge"
                      >
                        Perso.
                      </span>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
                  {[
                    { key: 'top',    label: 'Haut',     min: 4, max: 30 },
                    { key: 'bottom', label: 'Bas',      min: 4, max: 30 },
                    { key: 'left',   label: 'Gauche',   min: 4, max: 30 },
                    { key: 'right',  label: 'Droite',   min: 4, max: 30 },
                    { key: 'gutter', label: 'Gouttière', min: 4, max: 24 },
                  ].map((m) => (
                    <div key={m.key} className="rounded border p-2 bg-white">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-gray-500">{m.label}</span>
                        <span className="text-xs tabular-nums font-medium text-gray-800">
                          {Math.round(margins[m.key] ?? 8)} mm
                        </span>
                      </div>
                      <Slider
                        value={[Math.round(margins[m.key] ?? 8)]}
                        onValueChange={(v) => updateMargin(m.key, v[0])}
                        min={m.min}
                        max={m.max}
                        step={1}
                        className="mt-1"
                        data-testid={`sales-margin-${m.key}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                  La gouttière est l'espace entre les 3 zones (plan / 3D / infos). Min. 4 mm.
                </p>
              </div>
            </section>

            {/* Legend */}
            <section>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-800">🏷️ Légende symboles</h3>
                <Switch
                  checked={includeLegend}
                  onCheckedChange={setIncludeLegend}
                  data-testid="sales-include-legend"
                />
              </div>
              {includeLegend && (
                <div className="space-y-2">
                  {legendItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <Input
                        value={item.code}
                        onChange={(e) => {
                          const v = e.target.value.toUpperCase().slice(0, 5);
                          setLegendItems((p) => p.map((x, i) => (i === idx ? { ...x, code: v } : x)));
                        }}
                        placeholder="Code"
                        className="w-20 font-bold uppercase"
                        data-testid={`sales-legend-code-${idx}`}
                      />
                      <Input
                        value={item.label}
                        onChange={(e) => {
                          const v = e.target.value;
                          setLegendItems((p) => p.map((x, i) => (i === idx ? { ...x, label: v } : x)));
                        }}
                        placeholder="Description"
                        className="flex-1"
                        data-testid={`sales-legend-label-${idx}`}
                      />
                      <button
                        type="button"
                        onClick={() => setLegendItems((p) => p.filter((_, i) => i !== idx))}
                        className="text-red-500 text-xs hover:text-red-700"
                        title="Supprimer"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    onClick={() => setLegendItems((p) => [...p, { code: '', label: '' }])}
                    className="text-xs text-amber-600 hover:text-amber-700"
                    data-testid="sales-legend-add"
                  >
                    + Ajouter un symbole
                  </button>
                </div>
              )}
              <div className="flex items-center justify-between mt-3 rounded-lg border p-3 bg-gray-50">
                <div>
                  <Label className="text-xs">Plan de localisation lot dans bâtiment</Label>
                  <p className="text-[10px] text-gray-500">Mini-thumbnail bâtiment + repère du lot (utilise plan masse)</p>
                </div>
                <Switch
                  checked={includeLotLocation}
                  onCheckedChange={setIncludeLotLocation}
                  data-testid="sales-include-lot-location"
                />
              </div>
            </section>

            {/* Custom images gallery */}
            <section>
              <h3 className="text-sm font-semibold mb-2 text-gray-800">🖼️ Galerie d'images custom</h3>
              <p className="text-[11px] text-gray-500 mb-2">
                Images additionnelles affichées dans la bande basse des fiches lot (au-delà des FPS).
                Active "Strip mini-thumbnails bas" dans Contenus pour les afficher.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-2">
                {customImages.map((img) => (
                  <div key={img.image_id} className="relative group rounded border overflow-hidden bg-gray-50 aspect-square">
                    <img src={storageImg(img.path)} alt={img.label} className="w-full h-full object-cover" />
                    <button
                      onClick={() => deleteCustomImage(img.image_id)}
                      className="absolute top-1 right-1 bg-black/60 hover:bg-red-500 rounded p-0.5"
                      data-testid={`custom-image-delete-${img.image_id}`}
                    >
                      <X className="w-3 h-3 text-white" />
                    </button>
                    {img.label && (
                      <span className="absolute bottom-0 left-0 right-0 px-1 py-0.5 text-[9px] bg-black/60 text-white truncate">
                        {img.label}
                      </span>
                    )}
                  </div>
                ))}
                <label className="rounded border-2 border-dashed border-amber-300 bg-amber-50 hover:bg-amber-100 cursor-pointer aspect-square flex flex-col items-center justify-center gap-1 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => uploadCustomImage(e.target.files?.[0])}
                    className="hidden"
                    data-testid="sales-custom-image-input"
                  />
                  {uploadingAsset === 'custom' ? (
                    <Loader2 className="w-5 h-5 animate-spin text-amber-600" />
                  ) : (
                    <>
                      <Upload className="w-5 h-5 text-amber-600" />
                      <span className="text-[10px] text-amber-700">Ajouter</span>
                    </>
                  )}
                </label>
              </div>
            </section>

            {/* Content blocks */}
            <section>
              <h3 className="text-sm font-semibold mb-3 text-gray-800">🧩 Contenus à afficher</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                {Object.entries(BLOCK_LABELS).map(([k, label]) => (
                  <label
                    key={k}
                    className="flex items-center gap-2 px-2 py-1.5 rounded border bg-white hover:bg-amber-50 cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={blocks[k] || false}
                      onChange={() => toggleBlock(k)}
                      className="rounded"
                      data-testid={`sales-block-${k}`}
                    />
                    <span className="text-xs">{label}</span>
                  </label>
                ))}
              </div>
            </section>

            {/* Filters */}
            <section>
              <h3 className="text-sm font-semibold mb-3 text-gray-800">🔍 Filtres lots</h3>
              <div className="space-y-3">
                {availableBuildings.length > 0 && (
                  <div>
                    <Label className="text-xs">Bâtiments (vide = tous)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {availableBuildings.map((b) => (
                        <button
                          key={b.building_id}
                          type="button"
                          onClick={() => toggleArray(selectedBuildings, setSelectedBuildings, b.building_id)}
                          data-testid={`sales-filter-building-${b.building_id}`}
                          className="text-xs px-2 py-1 rounded-full border transition-colors"
                          style={
                            selectedBuildings.includes(b.building_id)
                              ? { background: accentColor, color: '#fff', border: `1px solid ${accentColor}` }
                              : { background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb' }
                          }
                        >
                          {b.name}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {availableTypologies.length > 0 && (
                  <div>
                    <Label className="text-xs">Typologies (vide = toutes)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {availableTypologies.slice(0, 30).map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => toggleArray(selectedTypologies, setSelectedTypologies, t)}
                          data-testid={`sales-filter-typo-${t}`}
                          className="text-xs px-2 py-1 rounded-full border transition-colors max-w-[200px] truncate"
                          style={
                            selectedTypologies.includes(t)
                              ? { background: accentColor, color: '#fff', border: `1px solid ${accentColor}` }
                              : { background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb' }
                          }
                          title={t}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {availableStatuses.length > 0 && (
                  <div>
                    <Label className="text-xs">Statuts workflow (vide = tous)</Label>
                    <div className="flex flex-wrap gap-1.5 mt-1">
                      {availableStatuses.map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => toggleArray(selectedStatuses, setSelectedStatuses, s)}
                          data-testid={`sales-filter-status-${s}`}
                          className="text-xs px-2 py-1 rounded-full border transition-colors"
                          style={
                            selectedStatuses.includes(s)
                              ? { background: accentColor, color: '#fff', border: `1px solid ${accentColor}` }
                              : { background: '#fff', color: '#6b7280', border: '1px solid #e5e7eb' }
                          }
                        >
                          {s}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Tri</Label>
                    <Select value={sortBy} onValueChange={setSortBy}>
                      <SelectTrigger data-testid="sales-sort">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SORT_OPTIONS.map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </section>
            </div>

            {/* === LIVE PREVIEW PANE === */}
            <aside
              className="rounded-xl overflow-hidden flex flex-col"
              style={{ background: '#1a1a1d', border: '1px solid #2a2a2e', minHeight: 0 }}
              data-testid="sales-brochure-preview"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b" style={{ borderColor: '#2a2a2e' }}>
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-amber-400" />
                  <span className="text-[12px] font-semibold text-amber-100">Aperçu live</span>
                  {previewLoading && (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-amber-300" />
                  )}
                </div>
                <label className="flex items-center gap-1.5 text-[11px] text-gray-300 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={previewEnabled}
                    onChange={(e) => setPreviewEnabled(e.target.checked)}
                    className="accent-amber-500"
                    data-testid="sales-preview-toggle"
                  />
                  Activer
                </label>
              </div>
              <div className="relative flex-1 min-h-[420px] bg-[#0d0d0f]">
                {previewError && (
                  <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
                    <div className="text-[12px] text-red-300 max-w-xs">
                      <span className="block font-semibold mb-1">Impossible de prévisualiser</span>
                      <span className="opacity-80">{previewError}</span>
                    </div>
                  </div>
                )}
                {!previewError && !previewUrl && (
                  <div className="absolute inset-0 flex items-center justify-center text-[11.5px] text-gray-500">
                    {previewEnabled ? 'Génération du premier aperçu…' : 'Aperçu désactivé'}
                  </div>
                )}
                {previewUrl && !previewError && (
                  <iframe
                    key={previewUrl}
                    src={`${previewUrl}#toolbar=0&navpanes=0&zoom=page-fit`}
                    title="Aperçu Plan de Vente"
                    className="w-full h-full"
                    style={{ background: '#0d0d0f', border: 'none' }}
                    data-testid="sales-preview-iframe"
                  />
                )}
              </div>
              <div className="px-3 py-2 text-[10.5px] text-gray-400 border-t" style={{ borderColor: '#2a2a2e' }}>
                Aperçu sur un lot exemple — page de garde masquée. Mise à jour automatique (800 ms).
              </div>
            </aside>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose}>Annuler</Button>
          <Button
            variant="outline"
            onClick={saveConfig}
            disabled={saving || loading}
            data-testid="sales-save-config"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Enregistrer config
          </Button>
          <Button
            onClick={generate}
            disabled={generating || loading}
            data-testid="sales-generate-btn"
            style={{ background: accentColor, color: '#fff' }}
            className="hover:opacity-90"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileDown className="w-4 h-4 mr-1" />}
            Générer le PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AssetSlot({ label, src, uploading, onUpload, onDelete, testid }) {
  return (
    <div className="rounded border p-2 bg-gray-50">
      <Label className="text-xs">{label}</Label>
      <div className="mt-1 flex items-center gap-2">
        {src ? (
          <img src={src} alt={label} className="w-20 h-14 object-cover rounded border bg-white" />
        ) : (
          <div className="w-20 h-14 rounded border bg-white flex items-center justify-center">
            <ImageIcon className="w-5 h-5 text-gray-300" />
          </div>
        )}
        <div className="flex-1">
          <label className="cursor-pointer">
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => onUpload(e.target.files?.[0])}
              data-testid={`${testid}-input`}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={uploading}
              data-testid={`${testid}-upload-btn`}
              asChild
            >
              <span>
                {uploading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-1" />
                ) : (
                  <Upload className="w-4 h-4 mr-1" />
                )}
                {src ? 'Remplacer' : 'Uploader'}
              </span>
            </Button>
          </label>
          {src && (
            <button
              type="button"
              onClick={onDelete}
              className="ml-2 text-[10px] text-red-500"
              data-testid={`${testid}-delete`}
            >
              retirer
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
